import httpClient from '@shared/lib/httpClient';
import { ENDPOINTS } from '@shared/services/api/endpoints';

/**
 * HTTP only — orchestration and state live in the hooks.
 * All calls go through httpClient so auth, base URL and 401 handling are applied.
 */

/**
 * Downloads the import template. The example rows the server returns depend on the caller's role.
 * @param {'xlsx'|'csv'} [format]
 * @returns {Promise<Blob>}
 */
export async function downloadSampleFile(format = 'xlsx') {
  const response = await httpClient.get(ENDPOINTS.USER_IMPORT.SAMPLE(format), {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Uploads a file for validation. Creates nothing — the response is the preview.
 * @param {File} file
 * @param {AbortSignal} [signal]
 * @returns {Promise<Object>} ApiResponse envelope
 */
export async function previewImport(file, signal) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await httpClient.post(ENDPOINTS.USER_IMPORT.PREVIEW, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response.data;
}

/**
 * Queues a fully valid draft. Rejected with 409 if any row is invalid.
 * @param {number} jobId
 * @returns {Promise<Object>} ApiResponse envelope
 */
export async function startImport(jobId) {
  const response = await httpClient.post(ENDPOINTS.USER_IMPORT.START(jobId));
  return response.data;
}

/**
 * @param {number} jobId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Object>} ApiResponse envelope with the job status
 */
export async function getImportStatus(jobId, signal) {
  const response = await httpClient.get(ENDPOINTS.USER_IMPORT.STATUS(jobId), { signal });
  return response.data;
}

/**
 * @param {number} jobId
 * @param {number} [page]
 * @param {number} [pageSize]
 * @returns {Promise<Object>} ApiResponse envelope with a page of rows
 */
export async function getImportRows(jobId, page = 1, pageSize = 25) {
  const response = await httpClient.get(ENDPOINTS.USER_IMPORT.ROWS(jobId, page, pageSize));
  return response.data;
}

/**
 * Downloads the rows a stopped run never created, in the import format.
 * @param {number} jobId
 * @param {'xlsx'|'csv'} [format]
 * @returns {Promise<Blob>}
 */
export async function downloadUnprocessedRows(jobId, format = 'xlsx') {
  const response = await httpClient.get(ENDPOINTS.USER_IMPORT.UNPROCESSED(jobId, format), {
    responseType: 'blob',
  });
  return response.data;
}
